import type { CommandFactory } from './types'
import { getStoredUser, fetchWithAuth } from '@/lib/api/client'
import type { MarketplaceListing } from '@shared/types/marketplace'

const API_URL = import.meta.env.VITE_API_URL

export const myListingsCommand: CommandFactory = (context) => ({
  id: 'my-listings',
  name: 'my-listings',
  description: 'View your marketplace listings and sales stats',
  acceptsArgs: false,
  handler: async () => {
    const user = await getStoredUser()
    if (!user?.id) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You must be logged in to view your listings.',
        timestamp: new Date(),
      })
      return
    }

    try {
      context.setIsSearching?.(true)

      const response = await fetchWithAuth(`${API_URL}/api/marketplace/my-listings`)

      if (!response.ok) {
        throw new Error('Failed to fetch listings')
      }

      const { listings, stats } = await response.json()

      if (listings.length === 0) {
        context.onDisplayMessage?.({
          role: 'command',
          content: `📦 **No Listings Yet**

You haven't listed any items for sale. Use \`/list-for-sale\` to create your first listing!`,
          timestamp: new Date(),
        })
        return
      }

      const listingsTable = listings
        .map((listing: MarketplaceListing, index: number) => {
          return `${index + 1}. **${listing.title}**
   - Type: ${listing.item_type}
   - Price: $${listing.price_usd.toFixed(2)}
   - Purchases: ${listing.purchase_count}
   - ID: \`${listing.id}\``
        })
        .join('\n\n')

      context.onDisplayMessage?.({
        role: 'command',
        content: `📊 **Your Marketplace Listings**

**Stats:**
- Total Revenue: $${stats.totalRevenue.toFixed(2)}
- Total Purchases: ${stats.totalPurchases}
- Active Listings: ${stats.activeListings}

---

**Your Listings:**

${listingsTable}

---

Use \`/list-for-sale\` to create more listings.`,
        timestamp: new Date(),
      })
    } catch (error) {
      context.onDisplayMessage?.({
        role: 'command',
        content: `❌ Failed to fetch listings: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      })
    } finally {
      context.setIsSearching?.(false)
    }
  },
})

export const marketplaceSearchCommand: CommandFactory = (context) => ({
  id: 'marketplace-search',
  name: 'marketplace-search',
  description: 'Search the marketplace for artifacts, themes, and identities',
  acceptsArgs: true,
  handler: async (args?: string) => {
    const user = await getStoredUser()
    if (!user?.id) {
      context.onDisplayMessage?.({
        role: 'command',
        content: '❌ You must be logged in to search the marketplace.',
        timestamp: new Date(),
      })
      return
    }

    if (!args || args.trim() === '') {
      context.onDisplayMessage?.({
        role: 'command',
        content: `⚠️ **Usage:** \`/marketplace-search <query>\`

**Example:** \`/marketplace-search dark theme\`

You can also ask the AI agent to search for you naturally, like:
- "Find me a dark theme for my desktop"
- "Search the marketplace for productivity artifacts"
- "Show me available AI identities"`,
        timestamp: new Date(),
      })
      return
    }

    try {
      context.setIsSearching?.(true)

      const query = args.trim()
      const params = new URLSearchParams({
        q: query,
      })

      const response = await fetchWithAuth(`${API_URL}/api/marketplace/search?${params}`)

      if (!response.ok) {
        throw new Error('Failed to search marketplace')
      }

      const { listings, total } = await response.json()

      if (listings.length === 0) {
        context.onDisplayMessage?.({
          role: 'command',
          content: `🔍 **No Results Found**

No items matched your search for "${query}".

Try:
- Using different keywords
- Browsing by type (artifact, theme, identity)
- Asking the AI agent to help you find what you're looking for`,
          timestamp: new Date(),
        })
        return
      }

      const resultsTable = listings
        .slice(0, 10)
        .map((listing: MarketplaceListing, index: number) => {
          return `${index + 1}. **${listing.title}**
   - Type: ${listing.item_type}
   - Price: $${listing.price_usd.toFixed(2)}
   - Purchases: ${listing.purchase_count}
   - Description: ${listing.description.slice(0, 100)}${listing.description.length > 100 ? '...' : ''}
   - ID: \`${listing.id}\``
        })
        .join('\n\n')

      context.onDisplayMessage?.({
        role: 'command',
        content: `🔍 **Search Results for "${query}"**

Found ${total} item${total !== 1 ? 's' : ''} (showing ${Math.min(listings.length, 10)}):

${resultsTable}

---

To purchase an item, ask the AI agent like:
"Purchase listing ${listings[0].id}"`,
        timestamp: new Date(),
      })
    } catch (error) {
      context.onDisplayMessage?.({
        role: 'command',
        content: `❌ Search failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date(),
      })
    } finally {
      context.setIsSearching?.(false)
    }
  },
})
